import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { useSetMemberPassword } from '../api';
import type { User } from '@/lib/api/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: User | null;
}

const MIN_LENGTH = 6;

/**
 * Manager-grade password reset. The backend enforces that only users with
 * User:full (and same company) can mutate someone else's password — this
 * dialog just collects the new password and surfaces any 4xx as a toast.
 *
 * The current password is NOT collected: that flow is for the user
 * editing their own password (Profile page), where the backend will
 * require oldPassword. Here we are an admin acting on a different user.
 */
export function SetPasswordDialog({ open, onOpenChange, member }: Props) {
  const t = useT();
  const setPassword = useSetMemberPassword();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      setPwd('');
      setConfirm('');
      setShow(false);
    }
  }, [open]);

  if (!member) return null;

  const tooShort = pwd.length > 0 && pwd.length < MIN_LENGTH;
  const mismatched = confirm.length > 0 && confirm !== pwd;
  const canSubmit =
    pwd.length >= MIN_LENGTH && pwd === confirm && !setPassword.isPending;

  const memberName =
    [member.firstname, member.lastname].filter(Boolean).join(' ') ||
    member.email;

  function submit() {
    if (!canSubmit || !member) return;
    setPassword.mutate(
      { id: member.id, newPassword: pwd },
      {
        onSuccess: () => {
          toast({
            tone: 'success',
            title: t('members.setPassword.successToast', { name: memberName }),
          });
          onOpenChange(false);
        },
        onError: (err: Error) =>
          toast({
            tone: 'error',
            title: t('members.setPassword.errorToast'),
            description: err.message,
          }),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('members.setPassword.title')}</DialogTitle>
          <DialogDescription>
            {t('members.setPassword.description', { name: memberName })}
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-4 py-2"
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
              {t('members.setPassword.newPassword')}
            </span>
            <div className="relative">
              <Input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                invalid={tooShort}
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                aria-label={
                  show
                    ? t('members.setPassword.hide')
                    : t('members.setPassword.show')
                }
                className="absolute inset-y-0 right-2 my-auto flex h-7 w-7 items-center justify-center rounded text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {tooShort && (
              <span className="text-[11px] text-[var(--color-error)]">
                {t('members.setPassword.minLength', { n: MIN_LENGTH })}
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
              {t('members.setPassword.confirm')}
            </span>
            <Input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              invalid={mismatched}
              autoComplete="new-password"
            />
            {mismatched && (
              <span className="text-[11px] text-[var(--color-error)]">
                {t('members.setPassword.mismatch')}
              </span>
            )}
          </label>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={setPassword.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit} loading={setPassword.isPending}>
              {t('members.setPassword.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
